import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDashboardSummary, useActiveAlerts } from '@/api/hooks/useAdmin';
import { formatCurrency } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import {
  CalendarDays,
  DollarSign,
  Activity,
  Bell,
  ChevronRight,
  MapPin,
} from 'lucide-react';

const PRIORITY_COLORS: Record<string, string> = {
  P1: 'bg-red-500 text-white',
  P2: 'bg-orange-500 text-white',
  P3: 'bg-yellow-500 text-black',
  P4: 'bg-gray-400 text-white',
};

const STORAGE_KEY = 'recplay_dashboard_location';

function timeAgo(dateStr: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t('admin.time_just_now');
  if (minutes < 60) return t('admin.time_minutes_ago', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('admin.time_hours_ago', { count: hours });
  return t('admin.time_days_ago', { count: Math.floor(hours / 24) });
}

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const [selectedLocationId, setSelectedLocationId] = useState<string>(
    () => localStorage.getItem(STORAGE_KEY) || 'all',
  );

  // Fetch "all locations" summary to populate the location selector
  const { data: allSummary } = useDashboardSummary();
  const locations = allSummary?.locationBreakdown ?? [];

  const locationId = selectedLocationId === 'all' ? undefined : selectedLocationId;
  const { data: summary, isLoading: summaryLoading } = useDashboardSummary(locationId);
  const { data: alertsData, isLoading: alertsLoading } = useActiveAlerts(5, locationId);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, selectedLocationId);
  }, [selectedLocationId]);

  // Reset to "all" if stored locationId no longer exists in loaded locations
  useEffect(() => {
    if (
      locations.length > 0 &&
      selectedLocationId !== 'all' &&
      !locations.some((l) => l.locationId === selectedLocationId)
    ) {
      setSelectedLocationId('all');
    }
  }, [locations, selectedLocationId]);

  const handleLocationChange = (value: string | null) => {
    setSelectedLocationId(value ?? 'all');
  };

  const selectedLabel =
    selectedLocationId === 'all'
      ? t('admin.all_locations')
      : locations.find((l) => l.locationId === selectedLocationId)?.locationName ??
        t('admin.all_locations');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold">{t('admin.dashboard')}</h1>

        {locations.length > 1 && (
          <Select value={selectedLocationId} onValueChange={handleLocationChange}>
            <SelectTrigger>
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{selectedLabel}</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('admin.all_locations')}</SelectItem>
              {locations.map((loc) => (
                <SelectItem key={loc.locationId} value={loc.locationId}>
                  {loc.locationName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Stat cards */}
      {summaryLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : summary ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Today's Bookings */}
          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <CalendarDays className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground truncate">{t('admin.today_bookings')}</p>
                <p className="text-2xl font-bold">{summary.todayBookings}</p>
              </div>
            </CardContent>
          </Card>

          {/* Today's Revenue */}
          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground truncate">{t('admin.today_revenue')}</p>
                <p className="text-2xl font-bold font-mono">
                  {formatCurrency(summary.todayRevenue, 'CAD', i18n.language)}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Active Sessions */}
          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/10">
                <Activity className="h-6 w-6 text-blue-600" />
                {summary.activeSessionsNow > 0 && (
                  <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-green-500 animate-pulse" />
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground truncate">{t('admin.active_sessions')}</p>
                <p className="text-2xl font-bold">{summary.activeSessionsNow}</p>
              </div>
            </CardContent>
          </Card>

          {/* Active Alerts */}
          <Card className={alertsData && alertsData.alerts.some(a => a.priority === 'P1' || a.priority === 'P2') ? 'border-red-200 bg-red-50/50' : ''}>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
                <Bell className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground truncate">{t('admin.active_alerts')}</p>
                <p className="text-2xl font-bold">{alertsData?.total || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Secondary stats */}
      {summary && (
        <div className="grid gap-4 sm:grid-cols-3">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-muted-foreground">{t('admin.upcoming_today')}</p>
              <p className="text-xl font-bold">{summary.upcomingBookingsToday}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-muted-foreground">{t('admin.no_shows')}</p>
              <p className="text-xl font-bold">{summary.noShowsToday}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-muted-foreground">{t('admin.cancellations')}</p>
              <p className="text-xl font-bold">{summary.cancellationsToday}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Unit status overview */}
      {summary && (
        <Card>
          <CardContent className="p-6">
            <h2 className="mb-4 text-sm font-semibold">{t('admin.unit_status')}</h2>
            <div className="flex flex-wrap gap-3">
              <Badge variant="secondary" className="bg-green-100 text-green-800 px-3 py-1">
                {summary.unitStatusCounts.online} {t('admin.online')}
              </Badge>
              <Badge variant="secondary" className="bg-red-100 text-red-800 px-3 py-1">
                {summary.unitStatusCounts.offline} {t('admin.offline')}
              </Badge>
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 px-3 py-1">
                {summary.unitStatusCounts.degraded} {t('admin.degraded')}
              </Badge>
              <Badge variant="secondary" className="bg-blue-100 text-blue-800 px-3 py-1">
                {summary.unitStatusCounts.maintenance} {t('admin.maintenance')}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent alerts */}
      <Card>
        <CardContent className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold">{t('admin.recent_alerts')}</h2>
            <Link
              to="/admin/alerts"
              className="flex items-center gap-1 text-xs text-primary hover:underline"
            >
              {t('admin.view_all_alerts')}
              <ChevronRight className="h-3 w-3" />
            </Link>
          </div>

          {alertsLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : alertsData && alertsData.alerts.length > 0 ? (
            <div className="space-y-3">
              {alertsData.alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="flex items-center gap-3 rounded-lg border p-3"
                >
                  <Badge className={`text-xs ${PRIORITY_COLORS[alert.priority]}`}>
                    {alert.priority}
                  </Badge>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{alert.title}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {alert.description}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {timeAgo(alert.createdAt, t)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {t('admin.no_active_alerts')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Location breakdown — only when viewing all locations */}
      {!locationId && summary && summary.locationBreakdown.length > 1 && (
        <Card>
          <CardContent className="p-6">
            <h2 className="mb-4 text-sm font-semibold">Locations</h2>
            <div className="space-y-2">
              {summary.locationBreakdown.map((loc) => (
                <div key={loc.locationId} className="flex items-center justify-between text-sm">
                  <span>{loc.locationName}</span>
                  <div className="flex gap-4 text-muted-foreground">
                    <span>{loc.todayBookings} bookings</span>
                    <span className="font-mono">{formatCurrency(loc.todayRevenue, 'CAD', i18n.language)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
